import React, { useState } from 'react';
import {
  Modal,
  Card,
  Button,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Typography,
  Tag,
  Popconfirm,
  App,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { VariantGroup, VariantSelectionMode } from '../../types/products';
import {
  useGetVariantGroupsQuery,
  useCreateVariantGroupMutation,
  useUpdateVariantGroupMutation,
  useDeleteVariantGroupMutation,
  useCreateProductVariantMutation,
  useDeleteProductVariantMutation,
  useUpdateProductVariantMutation,
} from '../../services/productsApi';

const { Text } = Typography;

interface VariantGroupsModalProps {
  visible: boolean;
  onCancel: () => void;
  productId: string;
  productName: string;
}

const SELECTION_MODE_OPTIONS: { value: VariantSelectionMode; label: string }[] = [
  { value: 'SingleSelect', label: 'Selección Única' },
  { value: 'MultiSelect', label: 'Selección Múltiple' },
  { value: 'QuantitySplit', label: 'Dividir Cantidad' },
];

const getModeBadgeColor = (mode: string) => {
  switch (mode) {
    case 'SingleSelect': return 'blue';
    case 'MultiSelect': return 'green';
    case 'QuantitySplit': return 'orange';
    default: return 'default';
  }
};

const getModeLabel = (mode: string) => {
  return SELECTION_MODE_OPTIONS.find(o => o.value === mode)?.label || mode;
};

const VariantGroupsModal: React.FC<VariantGroupsModalProps> = ({
  visible,
  onCancel,
  productId,
  productName,
}) => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const { data: groups = [], isLoading } = useGetVariantGroupsQuery(productId, { skip: !visible });
  const [createGroup] = useCreateVariantGroupMutation();
  const [updateGroup] = useUpdateVariantGroupMutation();
  const [deleteGroup] = useDeleteVariantGroupMutation();
  const [createVariant] = useCreateProductVariantMutation();
  const [updateVariant] = useUpdateProductVariantMutation();
  const [deleteVariant] = useDeleteProductVariantMutation();

  // State for adding new group
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMode, setNewGroupMode] = useState<VariantSelectionMode>('MultiSelect');
  const [newGroupRequired, setNewGroupRequired] = useState(false);

  // State for editing group
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupMode, setEditGroupMode] = useState<VariantSelectionMode>('MultiSelect');
  const [editGroupRequired, setEditGroupRequired] = useState(false);

  // State for adding variant to a group
  const [addingVariantToGroup, setAddingVariantToGroup] = useState<string | null>(null);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantPrice, setNewVariantPrice] = useState(0);

  // State for editing variant
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editVariantName, setEditVariantName] = useState('');
  const [editVariantPrice, setEditVariantPrice] = useState(0);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await createGroup({
        productId,
        name: newGroupName.trim(),
        selectionMode: newGroupMode,
        isRequired: newGroupRequired,
        sortOrder: groups.length,
      }).unwrap();
      message.success('Grupo creado');
      setShowNewGroup(false);
      setNewGroupName('');
      setNewGroupMode('MultiSelect');
      setNewGroupRequired(false);
    } catch {
      message.error('Error al crear grupo');
    }
  };

  const handleUpdateGroup = async (groupId: string) => {
    if (!editGroupName.trim()) return;
    try {
      await updateGroup({
        id: groupId,
        name: editGroupName.trim(),
        selectionMode: editGroupMode,
        isRequired: editGroupRequired,
        sortOrder: groups.find(g => g.id === groupId)?.sortOrder || 0,
      }).unwrap();
      message.success('Grupo actualizado');
      setEditingGroupId(null);
    } catch {
      message.error('Error al actualizar grupo');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroup(groupId).unwrap();
      message.success('Grupo eliminado');
    } catch {
      message.error('Error al eliminar grupo');
    }
  };

  const handleCreateVariant = async (groupId: string) => {
    if (!newVariantName.trim()) return;
    try {
      await createVariant({
        productId,
        name: newVariantName.trim(),
        priceModifier: newVariantPrice,
        variantGroupId: groupId,
      }).unwrap();
      message.success('Modificador agregado');
      setAddingVariantToGroup(null);
      setNewVariantName('');
      setNewVariantPrice(0);
    } catch {
      message.error('Error al agregar modificador');
    }
  };

  const handleUpdateVariant = async (variantId: string) => {
    if (!editVariantName.trim()) return;
    try {
      await updateVariant({
        id: variantId,
        name: editVariantName.trim(),
        priceModifier: editVariantPrice,
      }).unwrap();
      message.success('Modificador actualizado');
      setEditingVariantId(null);
    } catch {
      message.error('Error al actualizar modificador');
    }
  };

  const handleDeleteVariant = async (variantId: string) => {
    try {
      await deleteVariant(variantId).unwrap();
      message.success('Modificador eliminado');
    } catch (err: any) {
      message.error(err?.data?.message || 'Error al eliminar modificador');
    }
  };

  const startEditGroup = (group: VariantGroup) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
    setEditGroupMode(group.selectionMode);
    setEditGroupRequired(group.isRequired);
  };

  return (
    <Modal
      title={`Grupos de Modificadores - ${productName}`}
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="close" onClick={onCancel}>Cerrar</Button>,
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Add Group Button */}
        {!showNewGroup && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setShowNewGroup(true)}
            style={{ width: '100%', marginBottom: 16 }}
          >
            Agregar Grupo
          </Button>
        )}

        {/* New Group Form */}
        {showNewGroup && (
          <Card size="small" style={{ marginBottom: 16, border: '2px dashed #1890ff' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="Nombre del grupo (ej: Salsas, Tipo, Extras)"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                onPressEnter={handleCreateGroup}
                autoFocus
              />
              <Space wrap>
                <Select
                  value={newGroupMode}
                  onChange={setNewGroupMode}
                  options={SELECTION_MODE_OPTIONS}
                  style={{ width: 200 }}
                />
                <Space>
                  <Text>Obligatorio:</Text>
                  <Switch checked={newGroupRequired} onChange={setNewGroupRequired} size="small" />
                </Space>
              </Space>
              <Space>
                <Button type="primary" size="small" icon={<SaveOutlined />} onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
                  Crear
                </Button>
                <Button size="small" icon={<CloseOutlined />} onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>
                  Cancelar
                </Button>
              </Space>
            </Space>
          </Card>
        )}

        {/* Groups List */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>Cargando...</div>
        ) : groups.length === 0 && !showNewGroup ? (
          <Empty description="No hay grupos de modificadores" />
        ) : (
          groups.map(group => (
            <Card
              key={group.id}
              size="small"
              style={{ marginBottom: 12 }}
              title={
                editingGroupId === group.id ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      value={editGroupName}
                      onChange={e => setEditGroupName(e.target.value)}
                      size="small"
                    />
                    <Space wrap>
                      <Select
                        value={editGroupMode}
                        onChange={setEditGroupMode}
                        options={SELECTION_MODE_OPTIONS}
                        size="small"
                        style={{ width: 180 }}
                      />
                      <Space>
                        <Text style={{ fontSize: 12 }}>Obligatorio:</Text>
                        <Switch checked={editGroupRequired} onChange={setEditGroupRequired} size="small" />
                      </Space>
                    </Space>
                    <Space>
                      <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => handleUpdateGroup(group.id)}>
                        Guardar
                      </Button>
                      <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingGroupId(null)}>
                        Cancelar
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space>
                    <span>{group.name}</span>
                    <Tag color={getModeBadgeColor(group.selectionMode)}>{getModeLabel(group.selectionMode)}</Tag>
                    {group.isRequired && <Tag color="red">Obligatorio</Tag>}
                  </Space>
                )
              }
              extra={
                editingGroupId !== group.id && (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => startEditGroup(group)} />
                    <Popconfirm
                      title="Eliminar grupo"
                      description="Los modificadores del grupo no se eliminarán, quedarán sin grupo."
                      onConfirm={() => handleDeleteGroup(group.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )
              }
            >
              {/* Variants within group */}
              {group.variants.map(variant => (
                <div key={variant.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-color)' }}>
                  {editingVariantId === variant.id ? (
                    <Space style={{ flex: 1 }}>
                      <Input
                        value={editVariantName}
                        onChange={e => setEditVariantName(e.target.value)}
                        size="small"
                        style={{ width: 150 }}
                      />
                      <InputNumber
                        value={editVariantPrice}
                        onChange={v => setEditVariantPrice(v || 0)}
                        size="small"
                        style={{ width: 100 }}
                        prefix="$"
                      />
                      <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => handleUpdateVariant(variant.id)} />
                      <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingVariantId(null)} />
                    </Space>
                  ) : (
                    <>
                      <Text>{variant.name}</Text>
                      <Space>
                        <Text style={{ color: variant.priceModifier >= 0 ? '#52c41a' : '#ff4d4f' }}>
                          {variant.priceModifier >= 0 ? '+' : ''}{formatPrice(variant.priceModifier)}
                        </Text>
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingVariantId(variant.id);
                            setEditVariantName(variant.name);
                            setEditVariantPrice(variant.priceModifier);
                          }}
                        />
                        <Popconfirm
                          title="Eliminar modificador"
                          onConfirm={() => handleDeleteVariant(variant.id)}
                        >
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    </>
                  )}
                </div>
              ))}

              {/* Add variant to group */}
              {addingVariantToGroup === group.id ? (
                <Space style={{ marginTop: 8, width: '100%' }}>
                  <Input
                    placeholder="Nombre"
                    value={newVariantName}
                    onChange={e => setNewVariantName(e.target.value)}
                    size="small"
                    style={{ width: 150 }}
                    autoFocus
                  />
                  <InputNumber
                    placeholder="Precio"
                    value={newVariantPrice}
                    onChange={v => setNewVariantPrice(v || 0)}
                    size="small"
                    style={{ width: 100 }}
                    prefix="$"
                  />
                  <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => handleCreateVariant(group.id)} disabled={!newVariantName.trim()} />
                  <Button size="small" icon={<CloseOutlined />} onClick={() => { setAddingVariantToGroup(null); setNewVariantName(''); setNewVariantPrice(0); }} />
                </Space>
              ) : (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setAddingVariantToGroup(group.id)}
                  style={{ marginTop: 4 }}
                >
                  Agregar modificador
                </Button>
              )}
            </Card>
          ))
        )}
      </div>
    </Modal>
  );
};

export default VariantGroupsModal;
